import type { Person } from '@local/common-lib';
import {
  Check as CurrentIcon,
  ExpandMore as MenuIcon,
  SwapHoriz as SwitchIcon,
} from '@mui/icons-material';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { type FC, useState } from 'react';

interface Props {
  busy: boolean;
  /** Small screens: an icon that opens the menu, and nothing without a choice. */
  compact: boolean;
  current: Person.MyOrganization | undefined;
  onSelect: (organizationId: string) => void;
  organizations: Person.MyOrganization[];
}

/**
 * Shows which organization the session is acting in, and - for someone who
 * belongs to more than one - switches between them.
 *
 * On wide screens the name is always on display, not only once the menu is
 * open. On small screens there is no room beside the page title, so only a
 * switch icon is shown, and only when there is something to switch to; the
 * menu marks the current organization.
 */
export const OrganizationSwitcherView: FC<Props> = ({
  busy,
  compact,
  current,
  onSelect,
  organizations,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!current) return null;

  const canSwitch = organizations.length > 1;
  if (!canSwitch) {
    if (compact) return null;
    return (
      <Typography component="div" noWrap sx={{ opacity: 0.9 }} variant="body2">
        {current.name}
      </Typography>
    );
  }

  const label = `Organization: ${current.name}. Switch organization`;

  return (
    <>
      {compact ? (
        <IconButton
          aria-label={label}
          color="inherit"
          disabled={busy}
          edge="end"
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <SwitchIcon />
        </IconButton>
      ) : (
        <Button
          aria-label={label}
          color="inherit"
          disabled={busy}
          endIcon={<MenuIcon />}
          onClick={(event) => setAnchor(event.currentTarget)}
          size="small"
          sx={{ maxWidth: 260, textTransform: 'none' }}
        >
          <Typography component="span" noWrap variant="body2">
            {current.name}
          </Typography>
        </Button>
      )}
      <Menu
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        open={!!anchor}
        sx={{ zIndex: 10001 }}
      >
        {organizations.map((organization) => (
          <MenuItem
            key={organization._id}
            onClick={() => {
              setAnchor(null);
              if (organization._id === current._id) return;
              onSelect(organization._id);
            }}
            selected={organization._id === current._id}
          >
            <ListItemIcon>
              {organization._id === current._id ? <CurrentIcon /> : null}
            </ListItemIcon>
            <ListItemText
              primary={organization.name}
              secondary={organization.code}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
